import React, { useState, useEffect } from 'react';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BusinessIcon from '@mui/icons-material/Business';
import UploadIcon from '@mui/icons-material/CloudUpload';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import api from '../../config/api';
import { useSubsidiary } from '../../context/SubsidiaryContext';
import EmptyState from '../common/EmptyState';
import ConnectorsTab from './ConnectorsTab';
import SubsidiaryRulesManager from './SubsidiaryRulesManager';

// Tab panel component
function TabPanel({ children, value, index }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`subsidiary-tabpanel-${index}`}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const SubsidiaryManagement = () => {
  const {
    subsidiaries,
    loading,
    error,
    createSubsidiary,
    updateSubsidiary,
    deleteSubsidiary,
    refreshSubsidiaries
  } = useSubsidiary();

  const { confirmDialogProps, confirm } = useConfirmDialog();
  const [currentTab, setCurrentTab] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSubsidiary, setEditingSubsidiary] = useState(null);
  const [uploadingLogoFor, setUploadingLogoFor] = useState(null);
  const [logoError, setLogoError] = useState(null);
  // DT-1922: the create/edit dialog's own save error. The context's `error` (shown in the page Alert behind the modal)
  // cannot be cleared from here, so the dialog keeps the failed save's message itself.
  const [formError, setFormError] = useState(null);

  const API_BASE = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';

  const handleUploadLogo = async (subId, file) => {
    setUploadingLogoFor(subId);
    setLogoError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      await api.post(`${API_BASE}/api/subsidiaries/${subId}/logo`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await refreshSubsidiaries();
    } catch (err) {
      setLogoError(err.response?.data?.error || err.message || 'Logo upload failed');
    } finally {
      setUploadingLogoFor(null);
    }
  };

  const handleDeleteLogo = async (subId) => {
    if (!window.confirm('Remove this subsidiary\'s logo?')) return;
    try {
      await api.delete(`${API_BASE}/api/subsidiaries/${subId}/logo`);
      await refreshSubsidiaries();
    } catch (err) {
      setLogoError(err.response?.data?.error || err.message || 'Logo delete failed');
    }
  };

  // Per-subsidiary header/footer HTML branding for commercial invoices + packing lists.
  const [brandingSub, setBrandingSub] = useState(null);
  const [brandingForm, setBrandingForm] = useState({ header_html: '', footer_html: '', purchase_terms_html: '' });
  const [brandingSaving, setBrandingSaving] = useState(false);

  const openBranding = (subsidiary) => {
    setLogoError(null); setBrandingSub(subsidiary);
    setBrandingForm({
      header_html: subsidiary.header_html || '',
      footer_html: subsidiary.footer_html || '',
      purchase_terms_html: subsidiary.purchase_terms_html || '',
    });
  };

  const handleBrandingSave = async () => {
    if (!brandingSub) return;
    setBrandingSaving(true);
    setLogoError(null);
    try {
      await api.put(`${API_BASE}/api/subsidiaries/${brandingSub.id}/branding`, brandingForm);
      await refreshSubsidiaries();
      setBrandingSub(null);
    } catch (err) {
      setLogoError(err.response?.data?.error || err.message || 'Branding save failed');
    } finally {
      setBrandingSaving(false);
    }
  };
  // NOTE: these fields print on the Commercial Invoice / Warehouse Transfer /
  // Purchase Order / Goods Receipt / Wholesale templates (Seller & Buyer blocks),
  // which all read superchargeme.subsidiaries_v2. The backend PUT is a partial
  // patch that writes any key present in the body — including '' — so the edit
  // dialog MUST pre-fill every one of them from the list payload, or the first
  // save blanks whatever it didn't load.
  const EMPTY_FORM = {
    name: '',
    legal_name: '',
    country: '',
    currency: 'USD',
    description: '',
    registration_number: '',
    tax_id: '',
    vat_number: '',
    address_line1: '',
    address_line2: '',
    city: '',
    postal_code: '',
    phone: '',
    email: ''
  };

  const [formData, setFormData] = useState(EMPTY_FORM);

  useEffect(() => {
    refreshSubsidiaries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleOpenDialog = (subsidiary = null) => {
    setFormError(null);
    if (subsidiary) {
      setEditingSubsidiary(subsidiary);
      setFormData({
        name: subsidiary.name || '',
        legal_name: subsidiary.legal_name || '',
        country: subsidiary.country || '',
        currency: subsidiary.currency || 'USD',
        description: subsidiary.description || '',
        registration_number: subsidiary.registration_number || '',
        tax_id: subsidiary.tax_id || '',
        vat_number: subsidiary.vat_number || '',
        address_line1: subsidiary.address_line1 || '',
        address_line2: subsidiary.address_line2 || '',
        city: subsidiary.city || '',
        postal_code: subsidiary.postal_code || '',
        phone: subsidiary.phone || '',
        email: subsidiary.email || ''
      });
    } else {
      setEditingSubsidiary(null);
      setFormData({ ...EMPTY_FORM });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingSubsidiary(null);
    setFormData({ ...EMPTY_FORM });
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    setFormError(null);
    if (editingSubsidiary) {
      const result = await updateSubsidiary(editingSubsidiary.id, formData);
      if (result.success) {
        handleCloseDialog();
        refreshSubsidiaries();
      } else {
        setFormError(result.error || 'Failed to update subsidiary.');
      }
    } else {
      const result = await createSubsidiary(formData);
      if (result.success) {
        handleCloseDialog();
        refreshSubsidiaries();
      } else {
        setFormError(result.error || 'Failed to create subsidiary.');
      }
    }
  };

  const handleDelete = async (subsidiaryId, isDefault) => {
    if (isDefault) {
      alert('Cannot delete default subsidiary');
      return;
    }

    const confirmed = await confirm({
      title: 'Delete Subsidiary',
      message: 'Are you sure you want to delete this subsidiary? This action cannot be undone.',
      confirmText: 'Delete',
      severity: 'danger',
    });
    if (confirmed) {
      const result = await deleteSubsidiary(subsidiaryId);
      if (result.success) {
        refreshSubsidiaries();
      }
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Subsidiary Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage legal entities and connector relationships
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          disabled={loading}
        >
          Create Subsidiary
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      {logoError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setLogoError(null)}>
          {logoError}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Overview" />
          <Tab label="Connectors" />
          <Tab label="Rules" />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      <TabPanel value={currentTab} index={0}>
        {/* Overview Tab - Subsidiary Cards */}
        <Grid container spacing={3}>
          {subsidiaries.map((subsidiary) => (
            <Grid item xs={12} sm={6} md={4} key={subsidiary.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                    {subsidiary.logo_url ? (
                      <Box
                        component="img"
                        src={subsidiary.logo_url + '?t=' + Date.now()}
                        alt={`${subsidiary.name} logo`}
                        sx={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 0.5, border: '1px solid', borderColor: 'divider' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <BusinessIcon sx={{ color: 'primary.main' }} />
                    )}
                    <Typography variant="h6" sx={{ fontWeight: 600, flexGrow: 1 }}>
                      {subsidiary.name}
                    </Typography>
                    {subsidiary.is_default && (
                      <Chip label="Default" size="small" color="primary" />
                    )}
                  </Box>

                  {subsidiary.legal_name && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Legal: {subsidiary.legal_name}
                    </Typography>
                  )}

                  {subsidiary.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {subsidiary.description}
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    {subsidiary.country && (
                      <Chip label={subsidiary.country} size="small" variant="outlined" />
                    )}
                    {subsidiary.currency && (
                      <Chip label={subsidiary.currency} size="small" variant="outlined" />
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {subsidiary.connector_count || 0} connectors
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Box>
                    <Button
                      size="small"
                      component="label"
                      startIcon={uploadingLogoFor === subsidiary.id ? <CircularProgress size={14} /> : <UploadIcon sx={{ fontSize: 16 }} />}
                      disabled={uploadingLogoFor === subsidiary.id}
                      sx={{ textTransform: 'none' }}
                    >
                      {subsidiary.logo_url ? 'Replace logo' : 'Upload logo'}
                      <input
                        type="file"
                        hidden
                        accept="image/png,image/jpeg,image/webp,image/svg+xml"
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUploadLogo(subsidiary.id, f); e.target.value = ''; }}
                      />
                    </Button>
                    {subsidiary.logo_url && (
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteLogo(subsidiary.id)}
                        title="Remove logo"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => openBranding(subsidiary)}
                      title="Edit invoice header / footer HTML"
                      color={(subsidiary.header_html || subsidiary.footer_html) ? 'primary' : 'default'}
                    >
                      <BrandingWatermarkIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(subsidiary)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    {!subsidiary.is_default && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(subsidiary.id, subsidiary.is_default)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          ))}

          {subsidiaries.length === 0 && (
            <Grid item xs={12}>
              <EmptyState
                icon={BusinessIcon}
                title="No subsidiaries found"
                description="Create your first subsidiary (legal entity) to organize connectors"
                actionLabel="Create Subsidiary"
                actionIcon={<AddIcon />}
                onAction={() => handleOpenDialog()}
              />
            </Grid>
          )}
        </Grid>
      </TabPanel>

      <TabPanel value={currentTab} index={1}>
        {/* Connectors Tab */}
        <ConnectorsTab />
      </TabPanel>

      <TabPanel value={currentTab} index={2}>
        {/* Rules Tab */}
        <SubsidiaryRulesManager />
      </TabPanel>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSubsidiary ? 'Edit Subsidiary' : 'Create Subsidiary'}
        </DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its save fails; the page Alert sits behind the modal. */}
          {formError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFormError(null)}>{formError}</Alert>}
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Subsidiary Name"
              value={formData.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
              sx={{ mb: 2 }}
              required
            />
            <TextField
              fullWidth
              label="Legal Name"
              value={formData.legal_name}
              onChange={(e) => handleFormChange('legal_name', e.target.value)}
              sx={{ mb: 2 }}
            />
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Country"
                  value={formData.country}
                  onChange={(e) => handleFormChange('country', e.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Currency"
                  value={formData.currency}
                  onChange={(e) => handleFormChange('currency', e.target.value)}
                />
              </Grid>
            </Grid>
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => handleFormChange('description', e.target.value)}
              multiline
              rows={3}
              sx={{ mb: 3 }}
            />

            {/* Registered address + tax identity + contact. These print in the
                Seller/Buyer blocks of commercial invoices, POs, goods receipts,
                warehouse transfers and wholesale documents. */}
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Registered address
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Printed on commercial invoices, purchase orders, goods receipts and
              warehouse transfer documents.
            </Typography>
            <TextField
              fullWidth
              label="Address line 1"
              value={formData.address_line1}
              onChange={(e) => handleFormChange('address_line1', e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Address line 2"
              value={formData.address_line2}
              onChange={(e) => handleFormChange('address_line2', e.target.value)}
              sx={{ mb: 2 }}
            />
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.city}
                  onChange={(e) => handleFormChange('city', e.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Postal code"
                  value={formData.postal_code}
                  onChange={(e) => handleFormChange('postal_code', e.target.value)}
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Tax &amp; registration
            </Typography>
            <TextField
              fullWidth
              label="VAT number"
              value={formData.vat_number}
              onChange={(e) => handleFormChange('vat_number', e.target.value)}
              sx={{ mb: 2 }}
            />
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Company registration no."
                  value={formData.registration_number}
                  onChange={(e) => handleFormChange('registration_number', e.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Tax ID"
                  value={formData.tax_id}
                  onChange={(e) => handleFormChange('tax_id', e.target.value)}
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Contact
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={formData.phone}
                  onChange={(e) => handleFormChange('phone', e.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.name}
          >
            {editingSubsidiary ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Per-subsidiary branding HTML editor — header/footer snippets rendered on
          commercial invoices + packing lists. Raw HTML; owner-authored. */}
      <Dialog open={Boolean(brandingSub)} onClose={() => setBrandingSub(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Branding & terms — {brandingSub?.name}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            HTML rendered on every commercial invoice (header / footer) and
            optionally as the default Purchase Order T&amp;Cs (when neither the PO
            nor the supplier has their own). Raw HTML; empty = no snippet.
          </Typography>
        </DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {logoError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLogoError(null)}>{logoError}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Header HTML"
              multiline minRows={5}
              fullWidth
              value={brandingForm.header_html}
              onChange={(e) => setBrandingForm(f => ({ ...f, header_html: e.target.value }))}
              placeholder={'<div style="text-align:center"><strong>EXCITE FOUNDRY LTD</strong><br>Authorised distributor — Asia Pacific</div>'}
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12 } }}
              helperText="Renders directly above the document title."
            />
            <TextField
              label="Footer HTML"
              multiline minRows={5}
              fullWidth
              value={brandingForm.footer_html}
              onChange={(e) => setBrandingForm(f => ({ ...f, footer_html: e.target.value }))}
              placeholder={'<small>Bank: HSBC UK · Account 12345678 · Sort 40-00-00<br>Disputes: legal@yourdomain.com within 7 days of receipt.</small>'}
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12 } }}
              helperText="Renders after the standard seller-info footer."
            />
            <TextField
              label="Default Purchase Order T&Cs (HTML)"
              multiline minRows={8}
              fullWidth
              value={brandingForm.purchase_terms_html}
              onChange={(e) => setBrandingForm(f => ({ ...f, purchase_terms_html: e.target.value }))}
              placeholder={'<h2>Terms of Purchase</h2>\n<p><strong>1. PURCHASE ORDERS.</strong> Acknowledge within 7 calendar days...</p>'}
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12 } }}
              helperText="Rendered as the T&Cs appendix on every PO issued by this subsidiary — used only when neither the PO nor the supplier has more specific T&Cs. Up to 20 KB."
            />
            <Alert severity="info" sx={{ mt: 1 }}>
              <Typography variant="caption">
                Common uses: bank wire instructions, customs declaration text, marketing
                tagline, multiple addresses if you operate from more than one site.
                Up to 20 KB per field.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBrandingSub(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleBrandingSave} disabled={brandingSaving}>
            {brandingSaving ? <CircularProgress size={18} /> : 'Save branding'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog {...confirmDialogProps} />
    </Container>
  );
};

export default SubsidiaryManagement;
